import React from 'react';
import { Button } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { ChevronDown, ChevronUp, Download, FileSpreadsheet, Truck, Pencil, RefreshCw, Trash2, Receipt } from 'lucide-react';
import { STATUS_STYLES } from '../utils/constants';
import { calculateTotals, getTotalDiffColor, formatTons } from '../utils/helpers';

const DispatchHeader = ({
  dispatch,
  isExpanded,
  currentUser,
  getImportadoraName,
  onToggle,
  onDownloadDispatch,
  onDownloadNomina,
  onAssignTruck,
  onEditDispatch,
  onChangeStatus,
  onDeleteDispatch,
  onGenerateLiquidacion
}) => {
  const trucksList = dispatch.trucks || [];
  const canAssignMore = trucksList.length < (dispatch.truck_count || 1);
  const { totalKgLoaded, totalKgUnloaded, totalDiff } = calculateTotals(trucksList);
  const totalDiffColor = getTotalDiffColor(totalKgLoaded, totalKgUnloaded, totalDiff);

  // Check if there are trucks with complete data for liquidacion
  const hasCompleteTrucks = trucksList.some(t => 
    t.kg_loaded && t.kg_unloaded && t.destination && t.guide_number && t.license_plate
  );

  const isTransportista = currentUser.role === 'TRANSPORTISTA';
  const isDev = currentUser.role === 'DESARROLLO';
  const effectiveRole = isDev && currentUser.simulated_role ? currentUser.simulated_role : currentUser.role;
  const effectiveIsMaster = effectiveRole === 'MASTER';
  const effectiveIsAdmin = effectiveRole === 'ADMIN';
  const canTakeActions = !isTransportista && (effectiveIsMaster || effectiveIsAdmin);
  const isFinished = dispatch.status === 'Finalizado';

  return (
    <div 
      className={`px-3 py-2 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50 border-b border-slate-200' : 'hover:bg-slate-50/50'}`}
      onClick={onToggle}
    >
      <div className="flex items-center justify-between">
        {/* Info principal - columnas de ancho fijo para alineación entre tarjetas */}
        <div className="flex items-center text-xs min-w-0">
          {/* Despacho # */}
          <div className="flex items-center gap-1 w-[130px] shrink-0">
            {isExpanded ? <ChevronUp size={14} className="text-slate-400 shrink-0" /> : <ChevronDown size={14} className="text-slate-400 shrink-0" />}
            <span className="text-slate-400 text-[10px] uppercase">Desp.</span>
            <span className="font-semibold text-slate-800 font-mono truncate">{dispatch.dispatch_number}</span>
          </div>
          <span className="text-slate-300 shrink-0">|</span>
          {/* Estado */}
          <div className="flex items-center gap-1 w-[120px] shrink-0 px-2">
            <span className="text-slate-400 text-[10px] uppercase">Estado</span>
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border whitespace-nowrap ${STATUS_STYLES[dispatch.status] || 'bg-slate-100 text-slate-600'}`}>
              {dispatch.status}
            </span>
          </div>
          <span className="text-slate-300 shrink-0">|</span>
          {/* Importadora */}
          <div className="flex items-center gap-1 w-[180px] shrink-0 px-2">
            <span className="text-slate-400 text-[10px] uppercase shrink-0">Import.</span>
            <span className="text-slate-700 font-medium truncate">{getImportadoraName(dispatch.importadora_id)}</span>
          </div>
          <span className="text-slate-300 shrink-0">|</span>
          {/* CRT */}
          <div className="flex items-center gap-1 w-[120px] shrink-0 px-2">
            <span className="text-slate-400 text-[10px] uppercase shrink-0">CRT</span>
            <span className="text-slate-600 font-mono truncate">{dispatch.crt_number}</span>
          </div>
          <span className="text-slate-300 shrink-0">|</span>
          {/* Camiones */}
          <div className="flex items-center gap-1 w-[80px] shrink-0 px-2">
            <span className="text-slate-400 text-[10px] uppercase shrink-0">Cam.</span>
            <span className={`font-mono font-medium ${trucksList.length === dispatch.truck_count ? 'text-emerald-600' : 'text-amber-600'}`}>
              {trucksList.length}/{dispatch.truck_count}
            </span>
          </div>
          <span className="text-slate-300 shrink-0">|</span>
          {/* T. Cargadas */}
          <div className="flex items-center gap-1 w-[110px] shrink-0 px-2">
            <span className="text-slate-400 text-[10px] uppercase shrink-0">T.Carg.</span>
            <span className="text-slate-700 font-mono font-semibold">{formatTons(totalKgLoaded)}</span>
          </div>
          <span className="text-slate-300 shrink-0">|</span>
          {/* T. Descargadas */}
          <div className="flex items-center gap-1 w-[110px] shrink-0 px-2">
            <span className="text-slate-400 text-[10px] uppercase shrink-0">T.Desc.</span>
            <span className="text-slate-700 font-mono font-semibold">{formatTons(totalKgUnloaded)}</span>
          </div>
          <span className="text-slate-300 shrink-0">|</span>
          {/* Diferencia en toneladas */}
          <div className="flex items-center gap-1 w-[90px] shrink-0 px-2">
            <span className="text-slate-400 text-[10px] uppercase shrink-0">Dif.(T)</span>
            <span className={`font-mono font-bold ${totalDiffColor}`}>{totalKgLoaded && totalKgUnloaded ? formatTons(totalDiff) : '—'}</span>
          </div>
        </div>
        
        {/* Acciones del despacho - siempre visibles, gris cuando deshabilitadas */}
        {canTakeActions && (
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>

              {/* Generar Liquidación - MASTER: siempre visible, gris si no hay camiones completos */}
              {effectiveIsMaster && (() => {
                const active = hasCompleteTrucks;
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => active && onGenerateLiquidacion(dispatch)}
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${active ? 'text-emerald-600 hover:bg-emerald-50 cursor-pointer' : 'text-slate-300 cursor-not-allowed'}`}
                        data-testid={`generate-liquidacion-${dispatch.id}`}
                      >
                        <Receipt size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{active ? 'Generar Liquidación' : 'Sin camiones con datos completos'}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })()}

              {/* Descargar Despacho Excel - MASTER y ADMIN: siempre activo */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button onClick={() => onDownloadDispatch(dispatch)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-cyan-600 hover:bg-cyan-50">
                    <Download size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>Descargar Despacho (Excel)</p></TooltipContent>
              </Tooltip>

              {/* Descargar Nómina Excel - MASTER y ADMIN: siempre activo */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button onClick={() => onDownloadNomina(dispatch)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-orange-600 hover:bg-orange-50">
                    <FileSpreadsheet size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p>Descargar Nómina (Excel)</p></TooltipContent>
              </Tooltip>

              {/* Asignar Camión - MASTER y ADMIN: siempre visible, gris si despacho lleno o finalizado */}
              {(() => {
                const active = canAssignMore && !isFinished;
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => active && onAssignTruck(dispatch)}
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${active ? 'text-emerald-600 hover:bg-emerald-50 cursor-pointer' : 'text-slate-300 cursor-not-allowed'}`}
                        data-testid={`assign-truck-${dispatch.id}`}
                      >
                        <Truck size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{active ? 'Asignar Camión' : isFinished ? 'Despacho finalizado' : 'Cupo de camiones completo'}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })()}

              {/* Editar Despacho - MASTER: siempre visible, gris si finalizado */}
              {effectiveIsMaster && (() => {
                const active = !isFinished;
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => active && onEditDispatch(dispatch)}
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${active ? 'text-blue-600 hover:bg-blue-50 cursor-pointer' : 'text-slate-300 cursor-not-allowed'}`}
                        data-testid={`edit-dispatch-${dispatch.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{active ? 'Modificar Despacho' : 'Despacho finalizado'}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })()}

              {/* Cambiar Estado - MASTER: siempre activo */}
              {effectiveIsMaster && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={() => onChangeStatus(dispatch)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-violet-600 hover:bg-violet-50">
                      <RefreshCw size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Cambiar Estado</p></TooltipContent>
                </Tooltip>
              )}

              {/* Eliminar Despacho - MASTER: siempre activo */}
              {effectiveIsMaster && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={() => onDeleteDispatch(dispatch.id, dispatch.dispatch_number)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50">
                      <Trash2 size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent><p>Eliminar Despacho</p></TooltipContent>
                </Tooltip>
              )}

            </div>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};

export default DispatchHeader;
